// Vendor
import { FC, useState } from 'react';

// Components
import Button, { ButtonSize, ButtonVariant } from 'src/components/atoms/Button';
import { Row } from 'src/components/atoms/Row';
import Tag, { TagColor } from 'src/components/atoms/Tag';
import Text from 'src/components/atoms/Text';
import { ToolTip } from 'src/components/atoms/ToolTip';
import { ToolTipDirection } from 'src/components/atoms/ToolTip/ToolTip';
import OverrideIncomeAssetsModal from './OverrideIncomeAssetsModal';
import { StyledButton, StyledRow } from './styles';

// Helpers
import {
  getResultStatusColorTag,
  useTransformResultToReadable
} from 'src/features/fdeInbox/helpers';

// Enums
import { Feature } from 'src/ts/enums/feature';
import { IncomeVerificationSourceType } from 'src/ts/interfaces/entry';

// Types
import { EntryStatusBarProps } from './types';
// Hooks
import useUserRoles from 'src/features/auth/hooks/useUserRoles';
import usePropertyFeature from 'src/features/propertyFeature/hooks/usePropertyFeature';
import { currencyFormat } from 'src/helpers/currencyFormat';

const EntryStatusBar: FC<EntryStatusBarProps> = ({
  result,
  income,
  disableButton,
  onRemoveFromFolder,
  disputeMetadata,
  onIncomeCalculationDispute,
  isDisputesButtonDisabled,
  entryId,
  offerLetterPendingEmployerVerification,
  pendingSpecialtyReview
}) => {
  const { isAccountRepresentative, isAdmin, isAdminOrFdeManager, isSeniorFraudAnalyst } =
    useUserRoles();
  const { getIsPropertyFeatureActive } = usePropertyFeature();

  const transformResultToReadable = useTransformResultToReadable();
  const isIncomeVerificationEnabled = getIsPropertyFeatureActive(Feature.INCOME_VERIFICATION);

  const showGrossIncome = income && isIncomeVerificationEnabled;
  const isIncomePending = income?.state === 'Pending';
  const isPayrollOnly =
    disputeMetadata?.source_type === IncomeVerificationSourceType.CONNECTED_PAYROLL;
  const showDisputeButton =
    isIncomeVerificationEnabled &&
    disputeMetadata &&
    (isPayrollOnly ? isAdmin : isAdminOrFdeManager || isSeniorFraudAnalyst);
  // Backwards-compatible: the existing `!isIncomePending` cases (income
  // Available / Not Available) are unchanged. The OR only ADDS the case where
  // income is terminally Pending because the entry is OL-only with an offer
  // letter pending employer verification — an IV review will never run there,
  // so a manual override is the only resolution. The server already restricts
  // the override to OL-only PEV entries.
  const showOverrideButton =
    (isAdmin || isSeniorFraudAnalyst) &&
    !!income &&
    (!isIncomePending || !!offerLetterPendingEmployerVerification);

  // ITL-2900: while a specialty income review is in flight the report is held
  // for the specialty analyst. The override and dispute actions are disabled
  // (shown with a tooltip rather than hidden, so the lock is discoverable) —
  // otherwise an admin could release the held report and circumvent the
  // analyst (ITL-2708 SEC-1).
  const specialtyReviewLockTooltip = 'Disabled while a specialty income review is in progress.';

  const [showOverrideModal, setShowOverrideModal] = useState(false);

  return (
    <>
      <StyledRow data-testid="EntryStatusBar" justify="space-between" alignItems="center" gap={1}>
        <Row.Col>
          <Row alignItems="center" gap={0.625}>
            <Row.Col>
              <Text>Results</Text>
            </Row.Col>
            <Row.Col>
              <Tag
                label={transformResultToReadable(result)}
                color={getResultStatusColorTag(result)}
                showBackground
              />
            </Row.Col>
          </Row>
        </Row.Col>

        <Row.Col>
          <Row alignItems="center" gap={0.625}>
            {showGrossIncome && (
              <>
                <Row.Col>
                  <Text>Monthly Gross Income</Text>
                </Row.Col>
                <Row.Col>
                  <Row alignItems="center">
                    <Tag
                      label={
                        // While a specialty review is in flight, hide the
                        // calculated income from the leasing agent (ITL-2708).
                        pendingSpecialtyReview
                          ? 'Pending'
                          : income.value
                          ? currencyFormat(income.value)
                          : income.state || 'Not Available'
                      }
                      color={
                        pendingSpecialtyReview || !income.value ? TagColor.yellow : TagColor.green
                      }
                      showBackground
                    />
                  </Row>
                </Row.Col>
              </>
            )}
            {showDisputeButton && (
              <Row.Col>
                <ToolTip
                  direction={ToolTipDirection.bottom}
                  content={specialtyReviewLockTooltip}
                  hidden={!pendingSpecialtyReview}
                >
                  <StyledButton
                    name="dispute-entry-income-verification"
                    data-testid="dispute-income-calculation-btn"
                    aria-label="Dispute income calculation"
                    variant={ButtonVariant.contained}
                    size={ButtonSize.medium}
                    onClick={onIncomeCalculationDispute}
                    isDisabled={isDisputesButtonDisabled || pendingSpecialtyReview}
                  >
                    Dispute
                  </StyledButton>
                </ToolTip>
              </Row.Col>
            )}
            {showOverrideButton && (
              <Row.Col>
                <ToolTip
                  direction={ToolTipDirection.bottom}
                  content={specialtyReviewLockTooltip}
                  hidden={!pendingSpecialtyReview}
                >
                  <StyledButton
                    name="override-income-assets-btn"
                    variant={ButtonVariant.contained}
                    size={ButtonSize.medium}
                    onClick={() => setShowOverrideModal(true)}
                    isDisabled={pendingSpecialtyReview}
                  >
                    Override
                  </StyledButton>
                </ToolTip>
              </Row.Col>
            )}
          </Row>
        </Row.Col>

        <Row.Col>
          <Row alignItems="center" gap={0.625}>
            <Row.Col alignSelf="flex-end">
              {!isAccountRepresentative && (
                <Button
                  name="button-split-entry"
                  variant={ButtonVariant.outline}
                  size={ButtonSize.medium}
                  onClick={onRemoveFromFolder}
                  isDisabled={disableButton}
                >
                  Remove from Folder
                </Button>
              )}
            </Row.Col>
          </Row>
        </Row.Col>
      </StyledRow>

      <OverrideIncomeAssetsModal
        entryId={entryId}
        showModal={showOverrideModal}
        setShowModal={setShowOverrideModal}
      />
    </>
  );
};

export default EntryStatusBar;
